import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Label,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@ludolio/shared-ui';
import { SectionTitle } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import type { LabProjectDetail } from '@/lib/types';

const NO_UNIT = 'none';

export function SettingsPanel({
  detail,
  onChange,
  onDeleted,
}: {
  detail: LabProjectDetail;
  onChange: (detail: LabProjectDetail) => void;
  onDeleted: () => void;
}) {
  const labs = useLabs();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const currentUnit = detail.project.unit;
  const unitOptions = currentUnit && !labs.units.some((unit) => unit.id === currentUnit.id)
    ? [currentUnit, ...labs.units]
    : labs.units;

  const changeUnit = async (value: string) => {
    setBusy(true);
    try {
      onChange(await labsApi.updateProjectUnit(detail.project.id, value === NO_UNIT ? null : value));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const changeDrm = async (usesSdk: boolean) => {
    setBusy(true);
    try {
      onChange(await labsApi.updateProjectDrm(detail.project.id, !usesSdk));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await labsApi.deleteProject(detail.project.id);
      onDeleted();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <section className="space-y-8">
      <div>
        <SectionTitle>Unit</SectionTitle>
        <div className="space-y-2">
          <Label htmlFor="project-unit-setting">Linked unit</Label>
          <Select
            value={currentUnit?.id ?? NO_UNIT}
            onValueChange={(value) => void changeUnit(value)}
            disabled={busy || !detail.permissions.canChangeUnit}
          >
            <SelectTrigger id="project-unit-setting" className="sm:w-96">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_UNIT}>No unit</SelectItem>
              {unitOptions.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>{unit.code} · {unit.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!detail.permissions.canChangeUnit && (
            <p className="text-sm text-muted-foreground">
              {detail.role === 'owner' ? 'The unit is locked while a release is published or in review.' : 'Only a team owner can change the unit.'}
            </p>
          )}
        </div>
      </div>

      <div>
        <SectionTitle>Ludolio SDK</SectionTitle>
        <div className="space-y-2">
          <div className="flex h-9 items-center justify-between gap-4 sm:w-96">
            <Label htmlFor="project-sdk">Uses the Ludolio SDK</Label>
            <Switch
              id="project-sdk"
              checked={!detail.project.isDrmFree}
              disabled={busy || !detail.permissions.canChangeDrm}
              onCheckedChange={(checked) => void changeDrm(checked)}
            />
          </div>
          {!detail.permissions.canChangeDrm && (
            <p className="text-sm text-muted-foreground">Locked after the first published release.</p>
          )}
        </div>
      </div>

      {detail.permissions.canDelete && (
        <div>
          <SectionTitle>Delete project</SectionTitle>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-destructive/30 p-4">
            <p className="text-sm text-muted-foreground">Removes the project, its builds, and its images. This cannot be undone.</p>
            <Button variant="destructive" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete project</Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>Uploaded builds and images are removed. The team stays.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => void remove()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
